import {
  call,
  getContext,
  put,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";

import {
  getProfileSuccess,
  getProfileFailure,
  updateProfileSuccess,
  updateProfileFailure,
  uploadAvatarSuccess,
  uploadAvatarFailure,
  deleteAvatarSuccess,
  deleteAvatarFailure,
  uploadBannerSuccess,
  uploadBannerFailure,
  deleteBannerSuccess,
  deleteBannerFailure,
  disconnectMetamaskSuccess,
  disconnectMetamaskFailure,
  disconnectPayoutMethodSuccess,
  disconnectPayoutMethodFailure,
  disconnectPayPalSuccess,
  disconnectPayPalFailure,
  deleteAccountSuccess,
  deleteAccountFailure,
  setContentShareTourSuccess,
  setContentShareTourFailure,
  resetProfileUpcomingStream,
} from "./actions";
import {
  deleteAccountActionTypes,
  deleteAvatarActionTypes,
  deleteBannerActionTypes,
  disconnectMetamaskActionTypes,
  disconnectPayoutMethodActionTypes,
  disconnectPayPalActionTypes,
  getProfileActionTypes,
  setContentShareTourTypes,
  updateProfileActionTypes,
  uploadAvatarActionTypes,
  uploadBannerActionTypes,
} from "./actionTypes";
import { CONTEXT } from "@const/redux";
import { completeMultipartUpload, uploadFilesSaga } from "../upload/saga";

const getProfileAsync = ({ UsersService }) => {
  if (!UsersService) {
    console.log("Required services does not exist");

    return null;
  }

  return UsersService.getMyProfile();
};

function* getProfile() {
  try {
    const { UsersService } = yield getContext(CONTEXT.SERVICES);
    const { data } = yield call(getProfileAsync, { UsersService });

    yield put(getProfileSuccess(data));
    if (!data?.upcomingStream) {
      // we need to reset upcoming stream in case it was set before
      yield put(resetProfileUpcomingStream());
    }
  } catch ({ error }) {
    yield put(getProfileFailure(error));
  }
}

const updateProfileAsync = ({ UsersService, values }) => {
  if (!UsersService) {
    console.log("Required services does not exist");

    return null;
  }

  return UsersService.updateMyProfile(values);
};

function* updateProfile({ payload }) {
  try {
    const { UsersService } = yield getContext(CONTEXT.SERVICES);
    const { data } = yield call(updateProfileAsync, {
      UsersService,
      values: payload,
    });

    yield put(updateProfileSuccess(data));
  } catch ({ error }) {
    yield put(updateProfileFailure(error));
  }
}

const uploadAvatarAsync = async ({ UsersService, fileIds }) => {
  if (!UsersService) {
    console.log("Required services does not exist");

    return null;
  }

  const {
    data: { avatar },
  } = await UsersService.uploadAvatar({ fileId: fileIds[0] });

  return avatar;
};

function* uploadAvatar({ payload: base64 }) {
  try {
    const { UsersService, FilesService } = yield getContext(CONTEXT.SERVICES);
    const uploadedFiles = yield call(uploadFilesSaga, [base64]);
    yield call(completeMultipartUpload, { FilesService, uploadedFiles });
    const fileIds = uploadedFiles.map(({ fileId }) => fileId);
    const avatar = yield call(uploadAvatarAsync, {
      UsersService,
      fileIds,
    });

    yield put(uploadAvatarSuccess({ avatar }));
  } catch ({ error }) {
    yield put(uploadAvatarFailure(error));
  }
}

const deleteAvatarAsync = ({ UsersService }) => {
  if (!UsersService) {
    console.log("Required services does not exist");

    return null;
  }

  return UsersService.deleteAvatar();
};

function* deleteAvatar() {
  try {
    const { UsersService } = yield getContext(CONTEXT.SERVICES);
    yield call(deleteAvatarAsync, {
      UsersService,
    });

    yield put(deleteAvatarSuccess());
  } catch ({ error }) {
    yield put(deleteAvatarFailure(error));
  }
}

const uploadBannerAsync = async ({ UsersService, fileIds }) => {
  if (!UsersService) {
    console.log("Required services does not exist");

    return null;
  }

  const {
    data: { banner },
  } = await UsersService.uploadBanner({ fileId: fileIds[0] });

  return banner;
};

function* uploadBanner({ payload: file }) {
  try {
    const { UsersService, FilesService } = yield getContext(CONTEXT.SERVICES);
    const uploadedFiles = yield call(uploadFilesSaga, [file]);
    yield call(completeMultipartUpload, { FilesService, uploadedFiles });
    const fileIds = uploadedFiles.map(({ fileId }) => fileId);

    const banner = yield call(uploadBannerAsync, {
      UsersService,
      fileIds,
    });

    yield put(uploadBannerSuccess({ banner }));
  } catch ({ error }) {
    yield put(uploadBannerFailure(error));
  }
}

const deleteBannerAsync = ({ UsersService }) => {
  if (!UsersService) {
    console.log("Required services does not exist");

    return null;
  }

  return UsersService.deleteBanner();
};

function* deleteBanner() {
  try {
    const { UsersService } = yield getContext(CONTEXT.SERVICES);
    yield call(deleteBannerAsync, {
      UsersService,
    });

    yield put(deleteBannerSuccess());
  } catch ({ error }) {
    yield put(deleteBannerFailure(error));
  }
}

const disconnectMetamaskAsync = ({ SocialsService }) => {
  if (!SocialsService) {
    console.log("Required services does not exist");
    return;
  }

  return SocialsService.disconnectMetamask();
};

function* disconnectMetamask() {
  try {
    const { SocialsService } = yield getContext(CONTEXT.SERVICES);
    const { ToasterService } = yield getContext(CONTEXT.SERVICES);

    yield call(disconnectMetamaskAsync, { SocialsService });
    yield put(disconnectMetamaskSuccess());

    // todo: find a way to translate
    ToasterService.success(
      "MetaMask wallet has been successfully disconnected",
    );
  } catch ({ error }) {
    yield put(disconnectMetamaskFailure(error));
  }
}

const disconnectPayoutMethodAsync = ({ PaymentsService }) => {
  if (!PaymentsService) {
    return;
  }

  return PaymentsService.disconnectPayoutMethod();
};

const disconnectPayPalAsync = ({ PayPalService }) => {
  if (!PayPalService) {
    return;
  }

  return PayPalService.disconnectPayPal();
};

function* disconnectPayoutMethod() {
  try {
    const { PaymentsService } = yield getContext(CONTEXT.SERVICES);
    yield call(disconnectPayoutMethodAsync, { PaymentsService });
    yield put(disconnectPayoutMethodSuccess());
  } catch (error) {
    yield put(disconnectPayoutMethodFailure(error));
  }
}

function* disconnectPayPal() {
  try {
    const { PayPalService } = yield getContext(CONTEXT.SERVICES);
    const { ToasterService } = yield getContext(CONTEXT.SERVICES);

    yield call(disconnectPayPalAsync, { PayPalService });
    yield put(disconnectPayPalSuccess());

    // todo: find a way to translate
    ToasterService.success("PayPal account has been successfully disconnected");
  } catch (error) {
    yield put(disconnectPayPalFailure(error));
  }
}

const deleteAccountAsync = ({ UsersService, body }) => {
  if (!UsersService) {
    return;
  }

  return UsersService.deleteMyProfile(body);
};

function* deleteAccount({ payload }) {
  try {
    const { UsersService } = yield getContext(CONTEXT.SERVICES);
    yield call(deleteAccountAsync, { UsersService, body: { ...payload } });

    yield put(deleteAccountSuccess());
    window.location = "/";
  } catch (error) {
    yield put(deleteAccountFailure());
  }
}

const setContentShareTourAsync = ({ UsersService }) => {
  if (!UsersService) {
    return;
  }

  return UsersService.updateContentShareTourModel();
};

function* setContentShareTour() {
  try {
    const { UsersService } = yield getContext(CONTEXT.SERVICES);

    yield call(setContentShareTourAsync, { UsersService });

    yield put(setContentShareTourSuccess());
  } catch (error) {
    yield put(setContentShareTourFailure());
  }
}

export function* watchGetProfile() {
  yield takeLatest(getProfileActionTypes.request, getProfile);
}

export function* watchUpdateProfile() {
  yield takeEvery(updateProfileActionTypes.request, updateProfile);
}

export function* watchUploadAvatar() {
  yield takeEvery(uploadAvatarActionTypes.request, uploadAvatar);
}

export function* watchDeleteAvatar() {
  yield takeEvery(deleteAvatarActionTypes.request, deleteAvatar);
}

export function* watchUploadBanner() {
  yield takeEvery(uploadBannerActionTypes.request, uploadBanner);
}

export function* watchDeleteBanner() {
  yield takeEvery(deleteBannerActionTypes.request, deleteBanner);
}

export function* watchDisconnectMetamask() {
  yield takeEvery(disconnectMetamaskActionTypes.request, disconnectMetamask);
}

export function* watchDisconnectPayoutMethod() {
  yield takeEvery(
    disconnectPayoutMethodActionTypes.request,
    disconnectPayoutMethod,
  );
}

export function* watchDisconnectPayPal() {
  yield takeEvery(disconnectPayPalActionTypes.request, disconnectPayPal);
}

export function* watchDeleteAccount() {
  yield takeEvery(deleteAccountActionTypes.request, deleteAccount);
}

export function* watchSetContentShareTour() {
  yield takeEvery(setContentShareTourTypes.request, setContentShareTour);
}
